import {
  Alert,
  Button,
  Card,
  Dropdown,
  Empty,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
  Space,
  Spin
} from 'antd'
import Search from 'antd/lib/input/Search'
import dayjs from 'dayjs'
import React, { useEffect, useReducer, useState } from 'react'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useHistory } from 'react-router-dom'
import { useGetEmployees } from '../hooks/api/useEmployees'
import { BulkEmailDrawer } from '../components/BulkEmailDrawer'
import { EmployeeCard } from '../components/EmployeeCard'
import { useGetLookup } from '../hooks/api/useLookups'
import { useJsonExporter } from '../hooks/useExporter'
import { makeId } from '../hooks/uuid'
import { ExportMenu } from '../components/ExportMenu'
import { headers } from '../constants/reportHeaders'
import { useGetPerson } from '../hooks/api/usePerson'
import { useQueryParam } from '../hooks/useQueryParam'
dayjs.extend(relativeTime)
const { Option } = Select

const SelectionBar = ({ selectedLabel, selectedCount, onClear, onBulkImport, onBulkEmail }) => {
  if (!selectedCount) return null
  return (
    <Alert
      className='mb-4'
      message={selectedLabel}
      type='info'
      action={
        <Space>
          <Button type='link' onClick={onClear}>Clear</Button>
          <Button type='primary' className='bg-blue-400' shape='round' onClick={onBulkEmail}>Send bulk email</Button>
          <Button type='primary' className='bg-blue-400' shape='round' onClick={onBulkImport}>Bulk import Talent ID</Button>
        </Space>
      }
    />
  )
}

const filterReducer = (state, action) => {
  const { type, field, payload } = action
  switch (type) {
    case 'field': {
      return {
        ...state,
        [field]: payload,
        page: 1
      }
    }
    case 'page': {
      return {
        ...state,
        page: payload
      }
    }
    case 'select': {
      const next = new Map(state.selected)
      next.set(payload.uid, {
        uid: payload.uid,
        firstName: payload.person?.firstName || payload.firstName || '',
        lastName: payload.person?.lastName || payload.lastName || '',
        email: payload.email || ''
      })
      return {
        ...state,
        selected: next
      }
    }
    case 'deselect': {
      const next = new Map(state.selected)
      next.delete(payload.uid)
      return {
        ...state,
        selected: next
      }
    }
    case 'hydrate-from-url': {
      const next = new Map(payload.map(uid => [uid, { uid, firstName: '', lastName: '', email: '' }]))
      return {
        ...state,
        selected: next
      }
    }
    case 'clear-selection': {
      return {
        ...state,
        selected: new Map()
      }
    }
    case 'clear': {
      // client.invalidateQueries('employees')
      return {
        ...state,
        low: null,
        discipline: null,
        location: null,
        q: null,
        page: 1
      }
    }
    default: {
      throw new Error(`Unsupported action type: ${type}`)
    }
  }
}

export const Employees = () => {
  const [state, dispatch] = useReducer(filterReducer, { low: null, discipline: null, location: null, q: null, page: 1, selected: new Map() })
  const [execute, setExecute] = useState(false)
  const [type, setType] = useState('csv')
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false)
  const history = useHistory()
  const queryParam = useQueryParam()
  const employeesFilters = { low: state.low, discipline: state.discipline, location: state.location, q: state.q, page: state.page }
  // STATE
  const personQuery = useGetPerson({ authenticated: true })
  const employeesQuery = useGetEmployees(employeesFilters)
  const disciplineLookups = useGetLookup({ group: 'Dynamic', key: 'Discipline' })
  const locationLookups = useGetLookup({ group: 'Dynamic', key: 'JobLocation' })
  const lowLookups = useGetLookup({ group: 'Dynamic', key: 'LevelOfWork' })
  console.log('personQuery::: ', personQuery)
  useJsonExporter({
    execute,
    type,
    payload: employeesQuery?.data?.data,
    name: `employees-${makeId(6)}_${dayjs().format('DD/MM/YYYY')}`,
    callback: () => setExecute(false),
    headers: headers.employees
  })

  const isHRManager = personQuery?.data?.companyPerson?.admin?.roles?.includes('talent.HRManager')
  useEffect(() => {
    const selectedUids = (queryParam.get('selected') || '')
      .split(',')
      .map(uid => uid.trim())
      .filter(Boolean)
    if (selectedUids.length) {
      dispatch({ type: 'hydrate-from-url', payload: selectedUids })
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    const current = new URLSearchParams(window.location.search)
    const selectedUids = [...state.selected.keys()]
    if (selectedUids.length) {
      current.set('selected', selectedUids.join(','))
    } else {
      current.delete('selected')
    }
    const newUrl = `${window.location.pathname}${current.toString() ? '?' + current.toString() : ''}`
    window.history.replaceState({ path: newUrl }, '', newUrl)
  }, [state.selected])

  useEffect(() => {
    if (employeesQuery?.data?.data?.length) {
      employeesQuery.data.data.forEach(employee => {
        if (state.selected.has(employee.uid)) {
          dispatch({ type: 'select', payload: employee })
        }
      })
    }
  }, [employeesQuery?.data?.data]) // eslint-disable-line

  useEffect(() => {
    if (isHRManager) {
      const location = personQuery?.data?.companyPerson?.talentCompanyPerson?.jobLocation
      if (location) {
        dispatch({ type: 'field', field: 'location', payload: location })
      }
    }
    // eslint-disable-next-line
  }, [isHRManager])

  const selectedPeople = [...state.selected.values()]
  const selectedUids = [...state.selected.keys()]
  const employeesOnPage = employeesQuery?.data?.data || []
  const bulkEmailRecipients = selectedPeople.map((person) => {
    const employee = employeesOnPage.find(item => item.uid === person.uid)
    return {
      uid: person.uid,
      firstName: person.firstName || employee?.person?.firstName || '',
      lastName: person.lastName || employee?.person?.lastName || '',
      email: person.email || employee?.email || ''
    }
  })
  const selectedLabel = (() => {
    if (selectedPeople.length === 1) return `Selected: ${selectedPeople[0].firstName || '1 person'}`
    if (selectedPeople.length === 2) return `Selected: ${selectedPeople[0].firstName || '1'} & ${selectedPeople[1].firstName || '1'}`
    if (selectedPeople.length >= 3 && selectedPeople.length <= 4) return `Selected: ${selectedPeople[0].firstName || '1'}, ${selectedPeople[1].firstName || '1'} & ${selectedPeople.length - 2} others`
    if (selectedPeople.length >= 5) return `Selected: ${selectedPeople.length} people`
    return ''
  })()

  return (
    <div className='employees__page'>
      <PageHeader style={{ paddingLeft: 0 }} title='Employees' />
      <div className='w-full flex justify-between mb-6 mt-2'>
        <Space direction='horizontal'>
          <Space direction='vertical'>
            <p className='text-gray-400'>LoW</p>
            <Select onChange={(value) => dispatch({ type: 'field', field: 'low', payload: value })} placeholder='Select LoW' value={state.low} loading={lowLookups.isLoading} disabled={lowLookups.isError || !lowLookups.data} style={{ minWidth: 150 }}>
              <Option value={null}>None</Option>
              {!lowLookups.isLoading && lowLookups.data && lowLookups.data.map(option => (
                <Option key={option} value={option}>{option}</Option>
              ))}
            </Select>
          </Space>
          <Space direction='vertical'>
            <p className='text-gray-400'>Discipline</p>
            <Select onChange={(value) => dispatch({ type: 'field', field: 'discipline', payload: value })} placeholder='Select Discipline' value={state.discipline} loading={disciplineLookups.isLoading} disabled={disciplineLookups.isError || !disciplineLookups.data} style={{ minWidth: 150 }}>
              <Option value={null}>None</Option>
              {!disciplineLookups.isLoading && disciplineLookups.data && disciplineLookups.data.map(option => (
                <Option key={option} value={option}>{option}</Option>
              ))}
            </Select>
          </Space>
          <Space direction='vertical'>
            <p className='text-gray-400'>Location</p>
            <Select onChange={(value) => dispatch({ type: 'field', field: 'location', payload: value })} placeholder='Select location' value={state.location} loading={locationLookups.isLoading} disabled={isHRManager || locationLookups.isError || !locationLookups.data} style={{ minWidth: 150 }}>
              <Option value={null}>None</Option>
              {!locationLookups.isLoading && locationLookups.data && locationLookups.data.map(option => (
                <Option key={option} value={option}>{option}</Option>
              ))}
            </Select>
          </Space>
          {!isHRManager
            ? (
                (state.location || state.discipline || state.low) && <Button onClick={() => dispatch({ type: 'clear' })} className='mt-7 ml-4 bg-blue-400' type='primary' shape='round'>Clear</Button>
              )
            : null}
        </Space>
        <Space className='mt-7'>
          <Search onSearch={(value) => dispatch({ type: 'field', field: 'q', payload: value })} placeholder='Search Employee' style={{ width: 200 }} />
          <Dropdown overlay={<ExportMenu setType={setType} setExecute={setExecute} />}>
            <Button className='bg-blue-400' type='primary' shape='round'>
              Export
            </Button>
          </Dropdown>
        </Space>
      </div>
      <SelectionBar
        selectedLabel={selectedLabel}
        selectedCount={state.selected.size}
        onClear={() => dispatch({ type: 'clear-selection' })}
        onBulkEmail={() => setBulkEmailOpen(true)}
        onBulkImport={() => history.push(`/bulk-talent-id?selected=${selectedUids.join(',')}`)}
      />
      <BulkEmailDrawer
        visible={bulkEmailOpen}
        recipients={bulkEmailRecipients}
        onClose={() => setBulkEmailOpen(false)}
      />
      {employeesQuery.isFetching && (
        <div className='w-full flex justify-center py-4'>
          <Spin tip='Fetching employees...' />
        </div>
      )}
      {!employeesQuery.isLoading && employeesQuery.data.data
        ? (
          <>
            {employeesQuery.data.data !== undefined && employeesQuery.data.data?.length
              ? (
                <div className='pb-16'>

                  {employeesQuery?.data.data.map((employee, i) => (
                    <EmployeeCard
                      key={employee.uid}
                      employee={employee}
                      index={i}
                      selected={state.selected.has(employee.uid)}
                      onToggle={() => dispatch({ type: state.selected.has(employee.uid) ? 'deselect' : 'select', payload: employee })}
                    />
                  ))}

                  <Pagination defaultPageSize={50} showTotal={total => `Total: ${total} employees`} className='mt-8' defaultCurrent={state.page} onChange={(page) => dispatch({ type: 'page', payload: page })} total={employeesQuery.data.numberOfResults} hideOnSinglePage showSizeChanger={false} />
                </div>
                )
              : (
                <Empty />
                )}
          </>
          )
        : (
          <>
            <Card className='mb-8'>
              <Skeleton loading avatar active />
            </Card>
            <Card className='mb-8'>
              <Skeleton loading avatar active />
            </Card>
          </>
          )}
    </div>
  )
}
